import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, X } from "lucide-react";
import { requestCallback } from "../../features/public/callbackApi";
import { getFriendlyErrorMessage } from "../../app/apiClient";
import "./CallbackPopup.css";

function CallbackDialog({ setOpen }) {
  const dialog = useRef(null);
  const submitting = useRef(false);
  const prefix = useId();
  const [form, setForm] = useState({ fullName: "", phoneNumber: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const trigger = document.activeElement;
    const element = dialog.current;
    element.showModal();
    return () => {
      element.close();
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  const close = () => { if (!submitting.current) setOpen(false); };
  const change = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "phoneNumber" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setForm((current) => ({ ...current, [name]: nextValue }));
  };
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!form.fullName.trim() || !form.phoneNumber.trim()) {
      setError("Please enter your full name and phone number.");
      return;
    }
    if (!/^[0-9]{10}$/.test(form.phoneNumber)) {
      setError("Please enter a 10-digit phone number.");
      return;
    }
    submitting.current = true;
    setLoading(true);
    setError("");
    try {
      await requestCallback(form);
      setSuccess(true);
    } catch (err) {
      setError([401, 403].includes(err.response?.status)
        ? "Callback requests are currently unavailable. Please try again later."
        : getFriendlyErrorMessage(err, "We could not send your request. Please try again."));
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <dialog ref={dialog} className="callback-modal callback-login-style" aria-labelledby={`${prefix}-title`} aria-describedby={`${prefix}-description`}
      onCancel={(event) => { event.preventDefault(); close(); }}>
      <button type="button" className="callback-close" aria-label="Close callback request" disabled={loading} onClick={close}><X size={18} aria-hidden="true" /></button>
      <header className="callback-heading">
        <h2 id={`${prefix}-title`}>{success ? "Request received" : "Request a Call Back"}</h2>
        <p id={`${prefix}-description`}>{success ? "Thank you! Our representative will contact you soon." : "Leave your details and our representative will get in touch."}</p>
      </header>
      {success ? <div className="callback-success" role="status"><CheckCircle2 size={46} aria-hidden="true" /><p>Your callback request has been sent successfully.</p><button type="button" className="callback-submit" onClick={close}>Done</button></div> : (
        <form className="callback-form" onSubmit={submit} aria-busy={loading}>
          <fieldset disabled={loading}>
            {[
              ["fullName", "Full Name", "text", "name", "Enter your full name", true],
              ["phoneNumber", "Phone Number", "tel", "tel", "Enter your phone number", true],
              ["email", "Email (optional)", "email", "email", "Enter your email", false],
            ].map(([name, label, type, autoComplete, placeholder, required]) => <div className="callback-field" key={name}>
              <label htmlFor={`${prefix}-${name}`}>{label}</label>
              <input id={`${prefix}-${name}`} name={name} type={type} autoComplete={autoComplete} value={form[name]} onChange={change} placeholder={placeholder} required={required}
                {...(name === "phoneNumber" ? { inputMode: "numeric", maxLength: 10, pattern: "[0-9]{10}", title: "Enter a 10-digit phone number" } : {})} />
            </div>)}
            <div className="callback-field"><label htmlFor={`${prefix}-message`}>Message (optional)</label><textarea id={`${prefix}-message`} name="message" rows={3} value={form.message} onChange={change} placeholder="Tell us about your project" /></div>
          </fieldset>
          {error && <p className="callback-error" role="alert">{error}</p>}
          {loading && <p className="callback-progress" role="status">Sending your request...</p>}
          <button type="submit" className="callback-submit" disabled={loading}>{loading ? "Sending..." : "Request Callback"}</button>
        </form>
      )}
    </dialog>
  );
}

export default function CallbackPopup({ open, setOpen }) {
  return open ? createPortal(<CallbackDialog setOpen={setOpen} />, document.body) : null;
}
